// src/prototype/components/sep/SEPSectionExecutionRecommendation.tsx

import React from 'react';
import { ArrowRight, CheckCircle2, TrendingUp, Calendar } from 'lucide-react';
import { isQuickWin } from '../../config/opportunityBadge';

interface Finalist {
  id: string;
  title: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit: string };
}

export interface SEPSectionExecutionRecommendationProps {
  finalists: Finalist[];
  clientName: string;
  portfolioRoiLow: number;
  portfolioRoiHigh: number;
  executionHorizon: string;
}

const NextStep: React.FC<{ number: number; title: string; description: string; timeframe: string }> = ({ number, title, description, timeframe }) => (
  <div className="flex items-start gap-3">
    <div className="w-7 h-7 rounded-full bg-[#FF6E4C] flex items-center justify-center shrink-0 mt-0.5">
      <span className="text-[11px] font-bold text-white">{number}</span>
    </div>
    <div className="flex-1 border border-[#e5e7eb] rounded-xl p-3.5 bg-white">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-[#18365E]">{title}</p>
        <span className="text-[10px] font-semibold text-[#717182] bg-[#F5F7FA] px-2 py-0.5 rounded-full border border-[#e5e7eb] shrink-0 whitespace-nowrap">{timeframe}</span>
      </div>
      <p className="text-xs text-[#717182] leading-relaxed mt-1">{description}</p>
    </div>
  </div>
);

const fmt = (n: number) => `$${n.toFixed(1)}M`;

export const SEPSectionExecutionRecommendation: React.FC<SEPSectionExecutionRecommendationProps> = ({
  finalists, clientName, portfolioRoiLow, portfolioRoiHigh, executionHorizon,
}) => {
  // Quick Win = the value/feasibility quadrant, not stored effort.
  const quickWins = finalists.filter(f => isQuickWin({ id: f.id, implementationEffort: f.implementationEffort }));
  const pilots = finalists.filter(f => !isQuickWin({ id: f.id, implementationEffort: f.implementationEffort }));

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 10</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Execution Recommendation</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Strategic advisory conclusion and recommended next steps</p>
        </div>
      </div>

      {/* Narrative */}
      <div className="bg-[#18365E] rounded-xl p-5 mb-6 text-white">
        <p className="text-base font-bold mb-3 leading-snug">
          {clientName} is positioned to execute a high-confidence portfolio that delivers{' '}
          <span className="text-[#FF6E4C]">{fmt(portfolioRoiLow)}–{fmt(portfolioRoiHigh)}</span> in annual value over a{' '}
          <span className="text-[#FF6E4C]">{executionHorizon}</span> horizon.
        </p>
        <p className="text-sm text-white/75 leading-relaxed">
          The selected {finalists.length} initiatives represent the optimal intersection of strategic fit, ROI confidence, and organizational readiness.
          {quickWins.length > 0 && <> {quickWins.length} Quick Win initiative{quickWins.length !== 1 ? 's' : ''} should be activated immediately to generate early momentum and executive confidence.</>}
          {pilots.length > 0 && <> The remaining {pilots.length} program{pilots.length !== 1 ? 's' : ''} should enter structured pilot phases to validate assumptions before full investment commitment.</>}
        </p>
        <p className="text-sm text-white/75 leading-relaxed mt-2">
          This plan provides the documentation, sequencing, resource model, and governance framework required for board-level approval and sustainable delivery.
        </p>
      </div>

      {/* Key findings */}
      <div className="mb-6">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Advisory Summary</p>
        <div className="space-y-2.5 bg-[#F5F7FA] rounded-xl p-4 border border-[#e5e7eb]">
          {[
            `The portfolio has been assessed against ${clientName}'s strategic priorities and demonstrates strong alignment across clinical, operational, and financial objectives.`,
            `ROI assumptions are grounded in industry benchmarks and peer health system performance, providing a defensible basis for executive and board approval.`,
            `The phased execution model reduces organizational risk while delivering measurable value at multiple milestones throughout the ${executionHorizon} horizon.`,
            `Governance structures are designed to maintain leadership visibility, financial accountability, and course-correction capability throughout delivery.`,
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-[#FF6E4C] shrink-0 mt-px" />
              <p className="text-sm text-[#18365E]/85 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Next steps */}
      <div>
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Recommended Next Steps</p>
        <div className="space-y-3">
          <NextStep number={1} title="Executive Briefing & Portfolio Review" description="Present this Strategic Execution Plan to the CDO and CFO for strategic alignment sign-off before proceeding to full board submission." timeframe="Week 1" />
          <NextStep number={2} title="Board Approval Submission" description={`Submit the portfolio financial model and execution plan for board investment authorization. Estimated combined value: ${fmt(portfolioRoiLow)}–${fmt(portfolioRoiHigh)}.`} timeframe="Week 2–3" />
          {quickWins.length > 0 && <NextStep number={3} title={`Launch Quick Win: ${quickWins[0].title}`} description="Activate the Quick Win initiative immediately post-approval. Assign project lead, establish data access, and schedule sponsor kickoff within 5 business days." timeframe="Week 4" />}
          <NextStep number={quickWins.length > 0 ? 4 : 3} title="Initial Deployment Launch" description={pilots.length > 0 ? `Initiate Discovery phase for ${pilots.map(p => p.title.split(' ').slice(0, 3).join(' ')).join(' and ')} with defined phase gate milestones.` : 'Launch remaining initiatives according to the sequencing plan defined in this document.'} timeframe="Q2" />
        </div>
      </div>

      {/* Footer stamp */}
      <div className="mt-6 pt-5 border-t border-[#e5e7eb] flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-[#18365E]">Strategic Execution Plan</p>
          <p className="text-[10px] text-[#717182]">{clientName} · Prepared by DVA · Adaptive Product</p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#FF6E4C]/80">
          <span>Proceed to Command Center</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </div>
  );
};

export default SEPSectionExecutionRecommendation;